import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

export type SuiviStatut = 'depose' | 'partiel' | 'manquant';
export type SuiviSource = 'formateur' | 'scolarite' | 'mixte' | null;

export interface SuiviReleveLigne {
  matiere_id: number;
  code_matiere: string;
  nom_matiere: string;
  niveau: string;
  semestre_local: 1 | 2;
  ue_nom: string;
  option_nom: string;
  pole_nom: string;
  classe_code: string;
  enseignant: {
    id: number;
    nom_complet: string;
    telephone: string;
    email: string;
  };
  nb_notes: number;
  nb_etudiants_attendus: number;
  statut: SuiviStatut;
  source: SuiviSource;
  saisi_par_nom: string;
  saisi_par_role: string;
  date_premiere_saisie: string | null;
  date_derniere_saisie: string | null;
  pas_de_devoir_autorise?: boolean;
  pas_de_devoir_motif?: string;
}

export interface SuiviReleveResponse {
  annee_academique: string;
  semestre: 1 | 2;
  niveau: string | null;
  lignes: SuiviReleveLigne[];
  kpi: {
    total: number;
    depose: number;
    partiel: number;
    manquant: number;
    par_formateur: number;
    par_scolarite: number;
    mixte: number;
  };
}

export interface SuiviReleveParams {
  semestre: 1 | 2;
  niveau?: string;
  pole?: string;
  source?: 'all' | 'formateur' | 'scolarite' | 'manquant';
}

// ─── Sujets d'examen ─────────────────────────────────────────────────────────

export interface SuiviSujetLigne {
  matiere_id: number;
  code_matiere: string;
  nom_matiere: string;
  niveau: string;
  semestre_local: 1 | 2;
  ue_nom: string;
  option_nom: string;
  pole_nom: string;
  classe_code: string;
  enseignant: {
    id: number;
    nom_complet: string;
    telephone: string;
    email: string;
    user_id: number | null;
    a_compte_actif: boolean;
  };
  statut: SuiviStatut;
  nb_depose: number;
  nb_requis: number;
  derniere_date_depot: string | null;
  depose_par_nom: string;
  statut_depot: string;
}

export interface SuiviSujetResponse {
  annee_academique: string;
  semestre: 1 | 2;
  type_session: 'normale' | 'rattrapage';
  nb_requis: number;
  session_ouverte: boolean;
  session_libelle: string;
  lignes: SuiviSujetLigne[];
  kpi: { total: number; depose: number; partiel: number; manquant: number };
}

export interface SuiviSujetParams {
  semestre: 1 | 2;
  type_session: 'normale' | 'rattrapage';
  niveau?: string;
  pole?: string;
}

export interface RappelPayload {
  type: 'devoir' | 'sujet_normale' | 'sujet_rattrapage';
  cibles?: Array<{ user_id: number | null; matiere_id: number; matiere_nom?: string; code_matiere?: string }>;
  semestre?: 1 | 2;
  niveau?: string;
  pole?: string;
}

export interface RappelResponse {
  success: boolean;
  type: string;
  nb_formateurs_cibles: number;
  nb_push_envoyees: number;
  nb_sans_compte_formateur: number;
}

export const suiviScolariteApi = createApi({
  reducerPath: 'suiviScolariteApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['SuiviReleves', 'SuiviSujets'],
  endpoints: (builder) => ({
    getSuiviReleves: builder.query<SuiviReleveResponse, SuiviReleveParams>({
      query: (params) => ({
        url: '/suivi-scolarite/releves-devoirs/',
        params: params as any,
      }),
      providesTags: ['SuiviReleves'],
    }),
    getSuiviSujets: builder.query<SuiviSujetResponse, SuiviSujetParams>({
      query: (params) => ({
        url: '/suivi-scolarite/sujets-examens/',
        params: params as any,
      }),
      providesTags: ['SuiviSujets'],
    }),
    envoyerRappelFormateurs: builder.mutation<RappelResponse, RappelPayload>({
      query: (body) => ({
        url: '/suivi-scolarite/rappel-formateurs/',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useGetSuiviRelevesQuery,
  useGetSuiviSujetsQuery,
  useEnvoyerRappelFormateursMutation,
} = suiviScolariteApi;
